import { err, ok, Result, Unit } from "../result";
import { ProblemDetails } from "../models/Http";
import { del, get, post, put } from "./api";
import {
  AwaitingStoreOrderProposal,
  MergeStoreOrders,
  RemoveOrder,
  StoreOrder,
  StoreOrderId,
  StoreOrderState,
  UpdateStoreOrder
} from "../models/Store";

export const listAwaitingStoreOrderProposals = async (
  storeIds: string[] = []
): Promise<
  Result<ProblemDetails, ReadonlyArray<AwaitingStoreOrderProposal>>
> => {
  let additionalQuery = "";
  storeIds.forEach((val, index) => {
    additionalQuery =
      index === 0 ? `?storeIds=${val}` : `${additionalQuery}&storeIds=${val}`;
  });

  const response = await get(
    `/v1/stores/any/orders/awaiting-proposals${additionalQuery}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<AwaitingStoreOrderProposal>);
  }

  return err(content as ProblemDetails);
};

export const listOrders = async (
  storeId: string | "all",
  states: ReadonlyArray<StoreOrderState>,
  ignoreMerging: boolean = false
): Promise<Result<ProblemDetails, ReadonlyArray<StoreOrder>>> => {
  let statesQueryString = states.map(s => `states=${s}`).join("&");
  const response = await get(
    `/v1/stores/${storeId}/orders?ignoreMerging=${ignoreMerging}&${statesQueryString}`
  );
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<StoreOrder>);
  }

  return err(content as ProblemDetails);
};

export const getOrder = async (
  id: StoreOrderId
): Promise<Result<ProblemDetails, StoreOrder>> => {
  const response = await get(`/v1/stores/any/orders/${id}`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as StoreOrder);
  }

  return err(content as ProblemDetails);
};

export const updateOrder = async (
  storeOrderId: string,
  state: StoreOrderState
): Promise<Result<ProblemDetails, any>> => {
  const response = await put(`/v1/stores/any/orders/${storeOrderId}`, state);
  const content = await response.json();

  if (response.ok) {
    return ok(content);
  }
  return err(content as ProblemDetails);
};

export const addItem = async (
  storeOrderId: string,
  item: any
): Promise<Result<ProblemDetails, any>> => {
  const response = await post(
    `/v1/stores/any/orders/${storeOrderId}/items`,
    item
  );

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const updateItem = async (
  itemId: string,
  orderItem: any
): Promise<Result<ProblemDetails, any>> => {
  const response = await put(
    `/v1/stores/any/orders/items/${itemId}`,
    orderItem
  );

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const deleteItem = async (
  itemId: string
): Promise<Result<ProblemDetails, any>> => {
  const response = await del(`/v1/stores/any/orders/items/${itemId}`);

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const deliverItem = async (
  storeId: string,
  orderId: string,
  item: any
): Promise<Result<ProblemDetails, any>> => {
  const response = await post(
    `/v1/stores/${storeId}/orders/${orderId}/items/${item.id}/deliver`,
    item
  );

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  return ok({});
};

export const deliverOrder = async (
  storeOrderId: string,
  order: any
): Promise<Result<ProblemDetails, any>> => {
  const response = await post(
    `/v1/stores/any/orders/${storeOrderId}/deliver`,
    order
  );

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};

export const mergeOrders = async (
  action: MergeStoreOrders
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await put(`/v1/stores/any/orders/merge`, action);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};

export const generateOrder = async (
  storeId: string,
  distributors: string[]
): Promise<Result<ProblemDetails, {}>> => {
  const response = await post(`/v1/stores/${storeId}/orders/`, distributors);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};

export const regenerateOrder = async (
  id: StoreOrderId
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(`/v1/stores/any/orders/${id}/re-generate`);

  if (response.ok) {
    return ok({} as Unit);
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};

export const removeOrders = async (
  orders: ReadonlyArray<RemoveOrder>
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await del("/v1/stores/any/orders", {}, orders);

  if (response.ok) {
    return ok({} as Unit);
  }

  const content = await response.json();
  return err(content as ProblemDetails);
};
